import type { ProductVariant } from '../types';
import {
  isWirelessProtocol,
  isBundledPhysicalControlTag,
  PHYSICAL_CONTROLS,
} from './controlCompatibility';

export type ConfigStepKey =
  | 'tier'
  | 'power'
  | 'opening'
  | 'iface'
  | 'protocol'
  | 'control'
  | 'color'
  | 'profile_color'
  | 'motor_color'
  | 'size';

export type ProtocolGroup = {
  id: string;
  exclusive: boolean;
  options: string[];
  label?: string;
};

export type SelectorSchemaConfig = {
  steps?: ConfigStepKey[];
  protocols?: string[];
  colors?: string[];
  control_tags?: string[];
  profile_colors?: string[];
  motor_colors?: string[];
  protocol_groups?: ProtocolGroup[];
  dim_label?: string;
  dim_secondary_label?: string;
};

const norm = (s: string | null | undefined) =>
  String(s ?? '')
    .trim()
    .toLowerCase()
    .replace(/ё/g, 'е');

/** Сопоставление плитки конфигуратора с control_types из прайса (Фаза ↔ Фазное управление). */
export function controlTagMatches(
  variantTags: string[] | null | undefined,
  selected: string | null | undefined,
): boolean {
  const sn = norm(selected);
  if (!sn) return false;
  for (const raw of variantTags ?? []) {
    const tn = norm(raw);
    if (!tn) continue;
    if (tn === sn) return true;
    if (sn.includes('фаз') && tn.includes('фаз')) return true;
    if (sn.includes('485') && tn.includes('485')) return true;
    if (sn.includes('пульт') && tn.includes('пульт')) return true;
    if (sn.includes('сух') && tn.includes('сух')) return true;
    if (sn.includes('смартфон') && tn.includes('смартфон')) return true;
    if (sn.includes('контакт') && tn.includes('контакт')) return true;
  }
  return false;
}

/** ZigBee ↔ ZigBee 3.0 and similar prefix matches. */
export function protocolsMatch(
  variantProtocol: string | null | undefined,
  selected: string | null | undefined,
): boolean {
  const a = norm(variantProtocol);
  const b = norm(selected);
  if (!a || !b) return !selected;
  if (a === b) return true;
  if (a.startsWith(b) || b.startsWith(a)) return true;
  if (a.includes('zigbee') && b.includes('zigbee')) return true;
  if (a.includes('wifi') && b.includes('wifi')) return true;
  if (a.includes('matter') && b.includes('matter')) return true;
  return false;
}

export function parseSelectorSchema(raw: Record<string, unknown> | undefined | null): SelectorSchemaConfig {
  if (!raw || typeof raw !== 'object') return {};
  const steps = Array.isArray(raw.steps) ? (raw.steps as ConfigStepKey[]) : undefined;
  const protocol_groups = Array.isArray(raw.protocol_groups)
    ? (raw.protocol_groups as ProtocolGroup[]).filter((g) => g && Array.isArray(g.options))
    : undefined;
  return {
    steps,
    protocols: Array.isArray(raw.protocols) ? (raw.protocols as string[]) : undefined,
    colors: Array.isArray(raw.colors) ? (raw.colors as string[]) : undefined,
    control_tags: Array.isArray(raw.control_tags) ? (raw.control_tags as string[]) : undefined,
    profile_colors: Array.isArray(raw.profile_colors) ? (raw.profile_colors as string[]) : undefined,
    motor_colors: Array.isArray(raw.motor_colors) ? (raw.motor_colors as string[]) : undefined,
    protocol_groups,
    dim_label: typeof raw.dim_label === 'string' ? raw.dim_label : undefined,
  };
}

export type VariantLike = ProductVariant & {
  control_types?: string[];
  variant_specs?: Record<string, unknown>;
  tier_slug?: string | null;
  tier_id?: number | null;
};

export function variantProfileColor(v: VariantLike): string | null {
  const specs = v.variant_specs;
  if (specs && typeof specs.profile_color === 'string' && specs.profile_color) {
    return specs.profile_color;
  }
  return v.color ?? null;
}

export function variantMotorColor(v: VariantLike): string | null {
  const specs = v.variant_specs;
  if (specs && typeof specs.motor_color === 'string' && specs.motor_color) {
    return specs.motor_color;
  }
  return null;
}

export function variantOpeningType(v: VariantLike): string | null {
  const specs = v.variant_specs;
  if (specs && typeof specs.opening_type === 'string' && specs.opening_type.trim()) {
    return specs.opening_type.trim();
  }
  return null;
}

const physicalNorms = new Set(PHYSICAL_CONTROLS.map((t) => norm(t)));

export function variantMatchesSelection(
  v: VariantLike,
  sel: {
    tierSlug?: string | null;
    battery?: boolean | null;
    openingType?: string | null;
    ifaceType?: string | null;
    protocol?: string | null;
    wiredOnly?: boolean;
    controlTags?: string[];
    profileColor?: string | null;
    motorColor?: string | null;
    legacyColor?: string | null;
  },
): boolean {
  if (sel.tierSlug && norm(v.tier_slug) !== norm(sel.tierSlug)) return false;
  if (sel.battery !== null && sel.battery !== undefined && v.is_battery_powered !== sel.battery) {
    return false;
  }
  if (sel.openingType) {
    const vo = variantOpeningType(v);
    if (vo && norm(vo) !== norm(sel.openingType)) return false;
  }
  if (sel.ifaceType && norm(v.interface_type) !== norm(sel.ifaceType)) return false;
  if (sel.wiredOnly) {
    const p = String(v.protocol ?? '').trim();
    if (p && isWirelessProtocol(p)) return false;
  }
  if (sel.protocol && !protocolsMatch(v.protocol, sel.protocol)) return false;
  if (sel.controlTags && sel.controlTags.length > 0) {
    const vControls = v.control_types ?? [];
    const wirelessSku =
      isWirelessProtocol(v.protocol) && vControls.length === 0;
    for (const t of sel.controlTags) {
      if (!t) continue;
      if (wirelessSku && isBundledPhysicalControlTag(t)) continue;
      if (!controlTagMatches(vControls, t) && norm(v.interface_type) !== norm(t)) return false;
    }
  }
  const prof = sel.profileColor ?? sel.legacyColor;
  if (prof) {
    const pc = variantProfileColor(v);
    if (pc && norm(pc) !== norm(prof)) return false;
  }
  if (sel.motorColor) {
    const mc = variantMotorColor(v);
    if (mc && norm(mc) !== norm(sel.motorColor)) return false;
  }
  return true;
}

export function minVariantMatrixPrice(v: VariantLike): number {
  const m = v.price_matrix ?? [];
  const prices = m.map((p) => p.price).filter((p): p is number => typeof p === 'number' && Number.isFinite(p));
  return prices.length ? Math.min(...prices) : Number.POSITIVE_INFINITY;
}

/** Higher = tighter fit to user's control/protocol choices (prefer F4 for «только Фаза»). */
export function scoreVariantFit(
  v: VariantLike,
  sel: Parameters<typeof variantMatchesSelection>[1],
): number {
  let score = 0;
  const proto = String(v.protocol ?? '').trim();
  if (!sel.protocol && proto && isWirelessProtocol(proto)) return -1_000_000;

  if (sel.controlTags && sel.controlTags.length > 0) {
    const vControls = v.control_types ?? [];
    const vTags = new Set(vControls.map(norm));
    const wirelessSku =
      isWirelessProtocol(proto) && vControls.length === 0;
    for (const t of sel.controlTags) {
      if (!t) continue;
      if (wirelessSku && isBundledPhysicalControlTag(t)) continue;
      if (!vTags.has(norm(t)) && norm(v.interface_type) !== norm(t)) return -1_000_000;
    }
    for (const t of vTags) {
      if (!physicalNorms.has(t)) continue;
      if (!sel.controlTags.some((s) => norm(s) === t)) score -= 12;
    }
  }
  return score;
}

/** Standard vs Premium — only when both exist (раздвижные). Otherwise tier is implicit. */
export function needsTierConfiguratorStep(tierSlugs: string[]): boolean {
  const norms = tierSlugs.map((t) => norm(t)).filter(Boolean);
  if (norms.length === 0) return false;
  const hasPremium = norms.some((t) => t === 'premium' || t.includes('premium'));
  const hasStandard = norms.some((t) => t === 'standard' || t === 'standart');
  return hasPremium && hasStandard;
}

export function implicitTierSlug(tierSlugs: string[]): string | null {
  if (!tierSlugs.length) return null;
  const standard = tierSlugs.find((t) => {
    const n = norm(t);
    return n === 'standard' || n === 'standart';
  });
  return standard ?? tierSlugs[0] ?? null;
}

export function pickBestVariant(
  variants: VariantLike[],
  sel?: Parameters<typeof variantMatchesSelection>[1],
): VariantLike | null {
  if (!variants.length) return null;
  return [...variants].sort((a, b) => {
    if (sel) {
      const fa = scoreVariantFit(a, sel);
      const fb = scoreVariantFit(b, sel);
      if (fa !== fb) return fb - fa;
    }
    const pa = minVariantMatrixPrice(a);
    const pb = minVariantMatrixPrice(b);
    if (pa !== pb) return pa - pb;
    const ida = a.catalog_variant_id ?? a.id ?? 0;
    const idb = b.catalog_variant_id ?? b.id ?? 0;
    return ida - idb;
  })[0];
}

/** Раздвижные: единый конфигуратор (как «Прямой») для всех подтипов витрины. */
export const RAZDVIZHNYE_CONFIG_PRESET: SelectorSchemaConfig = {
  steps: ['tier', 'power', 'opening', 'control', 'profile_color', 'motor_color', 'size'],
  protocol_groups: [
    {
      id: 'wireless',
      exclusive: true,
      label: 'Беспроводные протоколы',
      options: ['Wi-Fi (TUYA)', 'ZigBee', 'Matter'],
    },
    {
      id: 'physical',
      exclusive: false,
      label: 'Физические интерфейсы',
      options: ['Фаза', 'RS-485', 'Сухой контакт', 'Пульт ДУ'],
    },
  ],
  control_tags: [],
  profile_colors: ['Белый', 'Чёрный', 'Серый'],
  motor_colors: ['Белый', 'Чёрный', 'Серебро'],
  protocols: ['ZigBee', 'Matter', 'Wi-Fi (TUYA)', 'RS-485'],
  dim_label: 'Ширина проёма, м',
};

/** @deprecated use RAZDVIZHNYE_CONFIG_PRESET */
export const PRYAMOY_CONFIG_PRESET = RAZDVIZHNYE_CONFIG_PRESET;

/** Пергола «Зимний сад»: только фазное управление, длина 1–6 м (прайс). */
export const PERGOLA_ZIMNIY_SAD_PRESET: SelectorSchemaConfig = {
  steps: ['power', 'control', 'profile_color', 'size'],
  protocol_groups: [],
  control_tags: ['Фазное управление'],
  profile_colors: ['Белый'],
  dim_label: 'Длина, м',
};

/** Уличная пергола со стойками: пульт ДУ, сетка длина × ширина. */
export const PERGOLA_ULICHNAYA_PRESET: SelectorSchemaConfig = {
  steps: ['power', 'control', 'size'],
  protocol_groups: [],
  control_tags: ['Пульт ДУ'],
  dim_label: 'Длина, м',
  dim_secondary_label: 'Ширина, м',
};

export function pergolaPresetForSubtype(subtypeSlug: string | null | undefined): SelectorSchemaConfig | null {
  const s = norm(subtypeSlug);
  if (!s) return null;
  if (s.includes('ulich')) return PERGOLA_ULICHNAYA_PRESET;
  if (s.includes('zimn') || s.includes('sad')) return PERGOLA_ZIMNIY_SAD_PRESET;
  return null;
}

export function isPergolaBranch(branchSlug: string | null | undefined, groupId?: string | null): boolean {
  const b = norm(branchSlug);
  if (b.includes('pergol')) return true;
  const gid = norm(groupId);
  return gid.startsWith('pergola::');
}

const COLOR_CHIP_LABELS = new Set(
  ['белый', 'черный', 'чёрный', 'серый', 'серебро', 'антрацит', 'шампань', 'золото'].map((s) =>
    s.toLowerCase(),
  ),
);

export function usesSplitColorSteps(config: SelectorSchemaConfig): boolean {
  const steps = config.steps ?? [];
  return steps.includes('profile_color') || steps.includes('motor_color');
}

function normalizeConfiguratorSteps(steps: ConfigStepKey[] | undefined): ConfigStepKey[] | undefined {
  if (!steps?.length) return steps;
  if (!usesSplitColorSteps({ steps })) return steps;
  return steps.filter((s) => s !== 'color');
}

function mergeSchemaPreset(
  parsed: SelectorSchemaConfig,
  preset: SelectorSchemaConfig,
  opts?: { forcePergolaTruth?: boolean },
): SelectorSchemaConfig {
  const steps = normalizeConfiguratorSteps(
    (parsed.steps?.length ? parsed.steps : preset.steps) as ConfigStepKey[] | undefined,
  );
  const force = opts?.forcePergolaTruth === true;
  const protocol_groups = force
    ? preset.protocol_groups
    : parsed.protocol_groups?.length
      ? parsed.protocol_groups
      : preset.protocol_groups;
  return {
    ...preset,
    ...parsed,
    steps,
    protocol_groups,
    control_tags: force
      ? preset.control_tags
      : parsed.control_tags?.length
        ? parsed.control_tags
        : preset.control_tags,
    profile_colors: force
      ? preset.profile_colors
      : parsed.profile_colors?.length
        ? parsed.profile_colors
        : preset.profile_colors,
    motor_colors: parsed.motor_colors?.length ? parsed.motor_colors : preset.motor_colors,
    protocols: force ? [] : parsed.protocols?.length ? parsed.protocols : preset.protocols,
    colors: parsed.colors?.length ? parsed.colors : preset.colors,
    dim_label: preset.dim_label ?? parsed.dim_label,
    ...(preset.dim_secondary_label ? { dim_secondary_label: preset.dim_secondary_label } : {}),
  };
}

function subtypeSlugFromGroupId(groupId: string | null | undefined): string | null {
  const gid = String(groupId ?? '').trim();
  if (!gid.includes('::')) return null;
  const parts = gid.split('::');
  return parts.length >= 2 ? parts[parts.length - 1] : null;
}

/** Client-side schema: раздвижные — пресет «Прямой»; перголы — фаза / пульт по подтипу. */
export function resolveSelectorSchema(
  raw: Record<string, unknown> | undefined | null,
  options?: {
    isSliding?: boolean;
    branchSlug?: string | null;
    subtypeSlug?: string | null;
    groupId?: string | null;
  },
): SelectorSchemaConfig {
  const parsed = parseSelectorSchema(raw);
  const subSlug = options?.subtypeSlug ?? subtypeSlugFromGroupId(options?.groupId);
  if (isPergolaBranch(options?.branchSlug, options?.groupId)) {
    const pergola = pergolaPresetForSubtype(subSlug);
    if (pergola) {
      return mergeSchemaPreset(parsed, pergola, { forcePergolaTruth: true });
    }
  }
  if (!options?.isSliding) {
    return { ...parsed, steps: normalizeConfiguratorSteps(parsed.steps) };
  }
  const hasGroups = Boolean(parsed.protocol_groups?.some((g) => g.options?.length));
  if (hasGroups) {
    return { ...parsed, steps: normalizeConfiguratorSteps(parsed.steps) };
  }
  return mergeSchemaPreset(parsed, RAZDVIZHNYE_CONFIG_PRESET);
}

export function isColorLikeControlTag(tag: string): boolean {
  return COLOR_CHIP_LABELS.has(norm(tag));
}

export function protocolGroupOptionSet(groups: ProtocolGroup[]): Set<string> {
  const out = new Set<string>();
  for (const g of groups) {
    for (const o of g.options) out.add(norm(o));
  }
  return out;
}

export type PowerFlags = { has_wired?: boolean; has_battery?: boolean };

export function dualPowerChoice(flags: PowerFlags | null | undefined): boolean {
  return Boolean(flags?.has_wired && flags?.has_battery);
}

export function wiredOnlyPower(flags: PowerFlags | null | undefined): boolean {
  return Boolean(flags?.has_wired && !flags?.has_battery);
}

/** Фильтр вариантов: 220/АКБ только после явного тапа в конфигураторе. */
export function effectiveBatterySelection(
  flags: PowerFlags | null | undefined,
  selected: boolean | null,
  powerTouched: boolean,
): boolean | null {
  if (!flags?.has_wired) return null;
  if (!powerTouched || selected === null) return null;
  return selected;
}

export function showWiredPowerChip(
  flags: PowerFlags | null | undefined,
  selected: boolean | null,
  powerTouched: boolean,
): boolean {
  return Boolean(flags?.has_wired && powerTouched && selected === false);
}
